/**
 * Roles, and what each one grants.
 *
 * The permission list is **not** hard-coded here. It comes from
 * `GET /admin/permissions`, which the server builds from the module manifests
 * — so a module that declares a permission has it appear in this editor
 * without anyone touching this file. A hand-maintained list would drift the
 * first time somebody added a module and forgot, and the symptom would be a
 * capability nobody could grant.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Can, useCan } from "@hardauto/core-access";
import { ApiError, NetworkError, type ApiClient } from "@hardauto/core-api";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  DataTable,
  Dialog,
  Input,
  Page,
  useToast,
  type Column,
} from "@hardauto/core-ui";
import { Field } from "@hardauto/core-forms";

import { AdminApi, type Permission, type Role } from "../api";
import { PermissionMatrix } from "./PermissionMatrix";
import { NAMESPACE } from "../i18n";

function describe(error: unknown, fallback: string): string {
  if (error instanceof NetworkError || error instanceof ApiError) return error.message;
  return fallback;
}

export function RolesScreen({ client }: { client: ApiClient }) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const api = useMemo(() => new AdminApi(client), [client]);
  const canManage = useCan("access.role.manage");

  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState<Role | "new" | null>(null);
  const [deleting, setDeleting] = useState<Role | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const [roleList, catalogue] = await Promise.all([api.listRoles(), api.listPermissions()]);
      setRoles(roleList.roles);
      setPermissions(catalogue.permissions);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: Column<Role>[] = [
    {
      key: "name",
      header: t("roles.name"),
      render: (role) => (
        <>
          {role.name}{" "}
          {/* Marked: a default profile's name and key are the code's, only what it may do
              is edited here. */}
          {role.is_system ? <Badge tone="info">{t("roles.system")}</Badge> : null}
        </>
      ),
    },
    {
      key: "key",
      header: t("roles.key"),
      secondary: true,
      render: (role) => <code>{role.key}</code>,
    },
    {
      key: "permissions",
      header: t("roles.permissions"),
      render: (role) => t("roles.permissionCount", { count: role.permissions.length }),
    },
    {
      key: "assigned",
      header: t("roles.assigned"),
      align: "end",
      secondary: true,
      render: (role) => role.assigned_users ?? 0,
    },
    {
      key: "actions",
      header: "",
      align: "end",
      render: (role) => (
        <Can permission="access.role.manage">
          {role.is_system && role.key === OWNER_KEY ? null : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setEditing(role);
                }}
              >
                {t("roles.edit")}
              </Button>
              {role.is_system ? null : (
                <Button
                  variant="ghost"
                  size="sm"
                  // Disabled rather than hidden when the role is in use: hiding
                  // it would leave the administrator wondering why this row has
                  // no delete, whereas a disabled control with an explanation
                  // beside it says what to do instead.
                  disabled={(role.assigned_users ?? 0) > 0}
                  title={
                    (role.assigned_users ?? 0) > 0
                      ? t("roles.inUse", { count: role.assigned_users ?? 0 })
                      : undefined
                  }
                  onClick={() => {
                    setDeleting(role);
                  }}
                >
                  {t("roles.delete")}
                </Button>
              )}
            </>
          )}
        </Can>
      ),
    },
  ];

  return (
    <Page
      title={t("roles.title")}
      description={t("roles.description")}
      actions={
        canManage ? (
          <Button
            variant="primary"
            onClick={() => {
              setEditing("new");
            }}
          >
            {t("roles.create")}
          </Button>
        ) : null
      }
    >
      <DataTable<Role>
        caption={t("roles.caption")}
        columns={columns}
        rows={roles}
        rowKey={(role) => role.id}
        loading={loading}
        error={
          failed
            ? {
                title: t("roles.loadFailed"),
                onRetry: () => void load(),
                retryLabel: t("error.retry"),
              }
            : undefined
        }
        empty={{ title: t("roles.empty"), description: t("roles.emptyDetail") }}
        loadingLabel={t("roles.loading")}
      />

      {/* The opposite question from the editor's: not "what does this role
          grant" but "who can refuse a warranty". Answering it from the editor
          means opening six dialogs and remembering. */}
      <PermissionMatrix roles={roles} permissions={permissions} />

      <RoleEditor
        api={api}
        permissions={permissions}
        role={editing}
        onClose={() => {
          setEditing(null);
        }}
        onSaved={(created) => {
          setEditing(null);
          toast.show(created ? t("roles.created") : t("roles.saved"), { tone: "success" });
          void load();
        }}
      />

      <Dialog
        open={deleting !== null}
        onClose={() => {
          setDeleting(null);
        }}
        title={t("roles.deleteTitle", { name: deleting?.name ?? "" })}
        description={t("roles.deleteBody")}
        closeLabel={t("roles.close")}
        footer={
          <>
            <Button
              onClick={() => {
                setDeleting(null);
              }}
            >
              {t("roles.cancel")}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                const target = deleting;
                if (!target) return;
                void (async () => {
                  await api.deleteRole(target.id);
                  setDeleting(null);
                  toast.show(t("roles.deleted"), { tone: "success" });
                  await load();
                })();
              }}
            >
              {t("roles.confirm")}
            </Button>
          </>
        }
      />
    </Page>
  );
}

/** The role holding the whole installation's administration: never edited from the screen. */
const OWNER_KEY = "owner";

/** `catalog.*` and `catalog.enrichment.*` as the permissions they cover; other keys as they are. */
function expandWildcards(granted: readonly string[], declared: readonly Permission[]): Set<string> {
  const result = new Set<string>();
  for (const key of granted) {
    if (key.endsWith(".*")) {
      const prefix = key.slice(0, -1);
      for (const permission of declared) {
        if (permission.key.startsWith(prefix)) result.add(permission.key);
      }
    } else {
      result.add(key);
    }
  }
  return result;
}

function RoleEditor({
  api,
  permissions,
  role,
  onClose,
  onSaved,
}: {
  api: AdminApi;
  permissions: Permission[];
  role: Role | "new" | null;
  onClose: () => void;
  onSaved: (created: boolean) => void;
}) {
  const { t } = useTranslation(NAMESPACE);
  const isNew = role === "new";
  const existing = role === "new" || role === null ? null : role;

  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [granted, setGranted] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (role === null) return;
    setError(null);
    setKey(existing?.key ?? "");
    setName(existing?.name ?? "");
    setDescription(existing?.description ?? "");
    // A default profile may grant by wildcard (`catalog.*`), which no checkbox shows: expanded
    // here into the permissions it stands for, so the editor shows what the role really holds.
    setGranted(expandWildcards(existing?.permissions ?? [], permissions));
  }, [role, existing, permissions]);

  // Grouped by the module that declared them. A flat list of forty keys is
  // unreadable; grouping makes "everything this module can do" a visible unit,
  // which is how administrators actually think about a role.
  const byModule = useMemo(() => {
    const groups = new Map<string, Permission[]>();
    for (const permission of permissions) {
      const list = groups.get(permission.module) ?? [];
      list.push(permission);
      groups.set(permission.module, list);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [permissions]);

  if (role === null) {
    return <Dialog open={false} onClose={onClose} title="" closeLabel={t("roles.close")} />;
  }

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (isNew) {
        await api.createRole({ key, name, description, permissions: [...granted] });
      } else if (existing) {
        await api.updateRole(existing.id, { name, description, permissions: [...granted] });
      }
      onSaved(isNew);
    } catch (caught) {
      setError(describe(caught, t("error.unexpected")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={isNew ? t("roles.createTitle") : t("roles.editTitle", { name: existing?.name ?? "" })}
      closeLabel={t("roles.close")}
      footer={
        <>
          <Button onClick={onClose}>{t("roles.cancel")}</Button>
          <Button
            variant="primary"
            busy={busy}
            busyLabel={t("roles.saving")}
            onClick={() => void submit()}
          >
            {t("roles.save")}
          </Button>
        </>
      }
    >
      {error ? <Alert tone="danger">{error}</Alert> : null}

      {isNew ? (
        <Field label={t("roles.key")} hint={t("roles.keyHint")} required>
          {(props) => (
            <Input
              {...props}
              value={key}
              onChange={(event) => {
                setKey(event.target.value);
              }}
            />
          )}
        </Field>
      ) : null}

      {existing?.is_system ? <Alert tone="info">{t("roles.systemHint")}</Alert> : null}

      <Field label={t("roles.name")} required>
        {(props) => (
          <Input
            {...props}
            disabled={existing?.is_system === true}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        )}
      </Field>

      {byModule.map(([module, group]) => (
        <fieldset key={module} className="hardauto-admin-group">
          {/* A real fieldset and legend: it is what tells a screen reader that
              these checkboxes belong together and what they have in common. */}
          <legend>{module}</legend>
          <ul className="hardauto-admin-checklist">
            {group.map((permission) => (
              <li key={permission.key}>
                <Checkbox
                  label={permission.label}
                  checked={granted.has(permission.key)}
                  onChange={(event) => {
                    setGranted((current) => {
                      const next = new Set(current);
                      if (event.target.checked) next.add(permission.key);
                      else next.delete(permission.key);
                      return next;
                    });
                  }}
                />
              </li>
            ))}
          </ul>
        </fieldset>
      ))}
    </Dialog>
  );
}
